import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Typography, Stack, Box, FormControl, Skeleton, Select, MenuItem } from '@mui/material'
import { TASK_MAP_SETTING_TYPES } from '../summaries/utils/constant'
import OtherOptions from '../../../dis-v2/_components/OtherOptions'
import { useJobActivityViewQuery } from '../job-summary/helper/reducers/jobAcitivityViewQuery'
import { datesValues, getConditionString } from 'utils/functions/conditionsOptions'
import { isEmpty } from 'lodash'
import { ProjectFormSelectComponent } from '../job-summary/JobSummaryForm'
import SelectFields from 'components/pages/dis-v2/_components/SelectFields'
import { TYPES_OF_TILE } from '../../../../../constants'

const GPS_KEYS = [
	'GPS_LocationLATITUDE',
	'GPS_LocationLONGITUDE',
	'GPS_LocationALTITUDE',
	'GPS_LocationACCURACY',
]
const GPS_LOCATION_COLUMN_KEY = 'GPS_Location'

function PlottingMapFormContent(props) {
	const { filterData, handleFilterDataChange, taskMapSettingType, hasSelectColumns, tileType } =
		props
	const [t] = useTranslation('common')

	const OTHER_OPTIONS = [
		{
			label: t('common:chart.showInDashboard'),
			value: 'showInDashboard',
		},
	]

	return (
		<Stack spacing={2}>
			{taskMapSettingType === TASK_MAP_SETTING_TYPES.GRID && (
				<GridForm filterData={filterData} handleFilterDataChange={handleFilterDataChange} />
			)}

			{taskMapSettingType === TASK_MAP_SETTING_TYPES.ALL && (
				<Stack spacing={2}>
					<GridForm
						filterData={filterData}
						handleFilterDataChange={handleFilterDataChange}
						hasSelectColumns={hasSelectColumns}
						tileType={tileType}
					/>

					{hasSelectColumns && (
						<OtherOptions
							optionList={OTHER_OPTIONS}
							checkedOptions={filterData?.otherOptionsChecked}
							migrateOtherOptions={filterData?.migrateOtherOptions}
							onChangeOptions={(value) => handleFilterDataChange('otherOptionsChecked', value)}
							onMigrateOtherOption={(value) => handleFilterDataChange('migrateOtherOptions', value)}
							tileType={filterData?.type}
						/>
					)}
				</Stack>
			)}
		</Stack>
	)
}

function GridForm(props) {
	const [t] = useTranslation('common')
	const { filterData, handleFilterDataChange, hasSelectColumns = false, tileType } = props
	const { allColumns, viewLoading } = useJobActivityViewQuery({
		formKey: filterData?.selectedTaskMapForm?.formKey,
		projectKey: filterData?.selectedTaskMapForm?.projectKey,
	})

	const DATE_OPTIONS = useMemo(() => {
		return datesValues(t).map((item) => ({
			label: item.name,
			value: getConditionString(item),
		}))
	}, [t])

	const displayColumns = useMemo(() => {
		const checked = filterData?.columnsChecked || []
		let baseColumns

		if (!hasSelectColumns) {
			baseColumns = [...allColumns]
		} else {
			if (isEmpty(checked)) return []
			baseColumns = allColumns.filter((col) => checked.includes(col.name))
		}

		const hasLat = baseColumns.some((col) => col.name === 'GPS_LocationLATITUDE')
		const hasLon = baseColumns.some((col) => col.name === 'GPS_LocationLONGITUDE')

		const hasRequired = hasLat && hasLon

		if (!hasRequired) return baseColumns

		const withoutGPS = baseColumns.filter((col) => !GPS_KEYS.includes(col.name))

		withoutGPS.unshift({
			name: GPS_LOCATION_COLUMN_KEY,
			title: 'GPS Location',
			type: 'CUSTOM',
			format: {},
		})

		return withoutGPS
	}, [hasSelectColumns, allColumns, filterData?.columnsChecked])

	return (
		<Stack spacing={2}>
			<Box sx={{ mb: '15px' }}>
				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{tileType === TYPES_OF_TILE.PLOTTING_MAP
						? 'Map Form'
						: tileType === TYPES_OF_TILE.TASK_SUMMARY_TILE
						? t('common:misc.taskMapForm')
						: t('common:misc.jobMapForm')}
				</Typography>

				<ProjectFormSelectComponent
					initProjectForm={filterData?.selectedTaskMapForm}
					onProjectFormChange={(value) => {
						handleFilterDataChange('selectedTaskMapForm', value)
					}}
				/>
			</Box>

			{filterData?.selectedTaskMapForm && hasSelectColumns && (
				<SelectFields
					columnList={allColumns}
					isColumnListLoading={viewLoading}
					primaryKeyList={filterData?.primaryKeyList}
					columnsChecked={filterData?.columnsChecked}
					otherOptionsChecked={[]}
					onChangeColumnsCheck={(value) => handleFilterDataChange('columnsChecked', value)}
					onChangePrimaryKeyList={(newPrimaryKeyList) => {
						handleFilterDataChange('primaryKeyList', newPrimaryKeyList)
					}}
					enableCheckAll={true}
					handleSettingsChange={handleFilterDataChange}
				/>
			)}

			{filterData?.selectedTaskMapForm && (
				<FormControl variant="outlined" size="small" fullWidth>
					<Typography variant="subtitle2" sx={{ pl: 1 }}>
						{t('common:misc.addressField')}
					</Typography>
					{viewLoading ? (
						<Skeleton variant="rectangular" height={'32px'} sx={{ mb: 1 }} />
					) : (
						<Select
							value={filterData?.taskMapAddressField || ''}
							onChange={(e) => {
								const newValue = e.target.value
								handleFilterDataChange('taskMapAddressField', newValue)
							}}
							size="small"
							fullWidth
						>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{displayColumns.map((col) => (
								<MenuItem value={col.name}>{col.title}</MenuItem>
							))}
						</Select>
					)}
				</FormControl>
			)}

			<FormControl variant="outlined" size="small" fullWidth>
				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{t('common:misc.dateFilter')}
				</Typography>

				<Select
					value={filterData?.taskMapDatePreset || 'ALL'}
					onChange={(e) => {
						const newValue = e.target.value
						handleFilterDataChange('taskMapDatePreset', newValue)
					}}
					size="small"
					fullWidth
				>
					{DATE_OPTIONS.map((option) => (
						<MenuItem value={option.value}>{option.label}</MenuItem>
					))}
				</Select>
			</FormControl>
			<Box sx={{ mt: 2 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{t('common:misc.bubbleMap')}
				</Typography>

				<Typography variant="subtitle2" sx={{ pl: 1 }}>
					{t('common:misc.bubbleField')}
				</Typography>
				{(filterData?.taskMapBubbleFields || []).map((field, index) => (
					<Box key={index} sx={{ mt: 1 }}>
						<Select
							value={field}
							onChange={(e) => {
								const newValue = e.target.value
								const updated = [...(filterData?.taskMapBubbleFields || [])]
								updated[index] = newValue
								handleFilterDataChange('taskMapBubbleFields', updated)
							}}
							size="small"
							fullWidth
						>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>

							{displayColumns.map((col) => (
								<MenuItem key={col.name} value={col.name}>
									{col.title}
								</MenuItem>
							))}
						</Select>
					</Box>
				))}
				<Box sx={{ mt: 1, pl: 1, display: 'flex', justifyContent: 'flex-end' }}>
					<Typography
						sx={{
							cursor: 'pointer',
							fontSize: '14px',
							color: '#1976d2',
						}}
						onClick={() => {
							const updated = [...(filterData?.taskMapBubbleFields || []), '']
							handleFilterDataChange('taskMapBubbleFields', updated)
						}}
					>
						+ Add Field
					</Typography>
				</Box>
			</Box>
		</Stack>
	)
}

function PlottingMapForm(props) {
	const { tileType } = props
	const [t] = useTranslation('common')
	return tileType === TYPES_OF_TILE.PLOTTING_MAP ? (
		<PlottingMapFormContent {...props} />
	) : (
		<div>
			<Typography variant="subtitle2" sx={{ pl: 1 }}>
				{tileType === TYPES_OF_TILE.TASK_SUMMARY_TILE &&
					`${t('common:misc.summaryTask')} ${t('common:misc.map')}`}
				{tileType === TYPES_OF_TILE.JOB_SUMMARY_TILE &&
					`${t('common:misc.summaryJob')} ${t('common:misc.map')}`}
			</Typography>
			<Box
				sx={{
					width: '100%',
					border: '1px solid #ddd',
					borderRadius: 1,
					p: 2,
					background: '#fff',
				}}
			>
				<PlottingMapFormContent {...props} />
			</Box>
		</div>
	)
}

export default PlottingMapForm
